/**
 * O tratamento do paciente na tela (#811, fase 4): o dinheiro, o desconto e o
 * formulário. Sem React, para o teste não precisar de tela.
 *
 * O valor anda em micros (milionésimos), como no resto da API: centavo em
 * ponto flutuante acumula erro. A conversão de "4.800,00" para micros e de
 * volta mora aqui, e é o que mais quebra em silêncio.
 */

import type {
  CriarTratamentoDoPaciente,
  EditarTratamentoDoPaciente,
  SituacaoDoTratamentoDaApi,
  TratamentoDoPaciente,
} from '@cliqcrm/contracts';

export const MICROS = 1_000_000;

export const NOMES_DA_SITUACAO: Readonly<Record<SituacaoDoTratamentoDaApi, string>> = {
  ativo: 'Em andamento',
  concluido: 'Concluído',
  interrompido: 'Interrompido',
};

/** `4_800_000_000` micros em BRL → "R$ 4.800,00". Com centavos: é preço de serviço. */
export function emReais(micros: number, moeda = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(
    micros / MICROS,
  );
}

/** "R$ 4.800,00", "4800", "4.800,5" → micros. `null` quando não é um valor. */
export function paraMicros(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/g, '');
  if (limpo === '') return null;
  // O ponto é de milhar; a vírgula, de centavos.
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/.test(limpo)) return null;
  const [inteiro = '0', centavos = ''] = limpo.replace(/\./g, '').split(',');
  return Number(inteiro) * MICROS + Number(centavos.padEnd(2, '0')) * (MICROS / 100);
}

/** micros → "4.800,00", para pôr num campo de texto. */
export function paraCampo(micros: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(micros / MICROS);
}

/** O desconto sobre a tabela, ou `null` se não há (ou se é acréscimo). */
export function desconto(
  tabelaMicros: number,
  negociadoMicros: number,
): { readonly micros: number; readonly porcento: number } | null {
  if (negociadoMicros >= tabelaMicros || tabelaMicros === 0) return null;
  const micros = tabelaMicros - negociadoMicros;
  return { micros, porcento: Math.round((micros / tabelaMicros) * 100) };
}

/** `2026-09-24` → `24/09/2026`. */
export function dataCurta(data: string): string {
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** A linha de fatos do tratamento: datas, profissional, pagamento e sessões. */
export function fatosDo(tratamento: TratamentoDoPaciente): string[] {
  const pagamento =
    tratamento.forma_pagamento && tratamento.parcelas
      ? `${tratamento.forma_pagamento} em ${tratamento.parcelas}x`
      : (tratamento.forma_pagamento ?? (tratamento.parcelas ? `em ${tratamento.parcelas}x` : null));
  return [
    tratamento.fechado_em ? `Fechado em ${dataCurta(tratamento.fechado_em)}` : null,
    tratamento.inicio_em ? `Início em ${dataCurta(tratamento.inicio_em)}` : null,
    tratamento.medico?.nome ?? null,
    pagamento,
    tratamento.sessoes_previstas ? `${tratamento.sessoes_previstas} sessões` : null,
  ].filter((parte): parte is string => parte !== null);
}

/** Os campos do formulário, como texto: o vazio vira `null` no corpo. */
export interface FormularioDoTratamento {
  readonly fechadoEm: string;
  readonly inicioEm: string;
  readonly medicoId: string;
  readonly formaPagamento: string;
  readonly parcelas: string;
  readonly sessoes: string;
}

export const FORMULARIO_VAZIO: FormularioDoTratamento = {
  fechadoEm: '',
  inicioEm: '',
  medicoId: '',
  formaPagamento: '',
  parcelas: '',
  sessoes: '',
};

export function formularioDe(tratamento: TratamentoDoPaciente): FormularioDoTratamento {
  return {
    fechadoEm: tratamento.fechado_em ?? '',
    inicioEm: tratamento.inicio_em ?? '',
    medicoId: tratamento.medico?.id ?? '',
    formaPagamento: tratamento.forma_pagamento ?? '',
    parcelas: tratamento.parcelas?.toString() ?? '',
    sessoes: tratamento.sessoes_previstas?.toString() ?? '',
  };
}

const ouNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());
const inteiroOuNulo = (texto: string): number | null =>
  /^\d+$/.test(texto.trim()) ? Number(texto.trim()) : null;

/** Os campos comuns de criar e editar. */
export function camposDoCorpo(
  formulario: FormularioDoTratamento,
): Omit<EditarTratamentoDoPaciente, 'situacao' | 'versao'> {
  return {
    fechado_em: ouNulo(formulario.fechadoEm),
    inicio_em: ouNulo(formulario.inicioEm),
    medico_id: ouNulo(formulario.medicoId),
    forma_pagamento: ouNulo(formulario.formaPagamento),
    parcelas: inteiroOuNulo(formulario.parcelas),
    sessoes_previstas: inteiroOuNulo(formulario.sessoes),
  };
}

/** O corpo do cadastro. O valor igual ao de tabela vai como `null`: vale a tabela. */
export function corpoParaCriar(entrada: {
  readonly formulario: FormularioDoTratamento;
  readonly tratamentoId: string;
  readonly oportunidadeId: string;
  readonly valorTabelaMicros: number;
  readonly valorNegociadoMicros: number | null;
  readonly motivo: string;
  /** Os campos personalizados preenchidos no cadastro (#568). */
  readonly campos?: CriarTratamentoDoPaciente['campos'];
}): CriarTratamentoDoPaciente {
  const negociado =
    entrada.valorNegociadoMicros === null ||
    entrada.valorNegociadoMicros === entrada.valorTabelaMicros
      ? null
      : entrada.valorNegociadoMicros;
  return {
    tratamento_id: entrada.tratamentoId,
    oportunidade_id: ouNulo(entrada.oportunidadeId),
    valor_negociado_micros: negociado,
    motivo: negociado === null ? null : ouNulo(entrada.motivo),
    ...camposDoCorpo(entrada.formulario),
    ...(entrada.campos && Object.keys(entrada.campos).length > 0 ? { campos: entrada.campos } : {}),
  };
}
